import { observer } from 'mobx-react';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';

import SelectSearch, {
  DomProps,
  OptionSnapshot,
  SelectedOption,
  Snapshot,
  ValueProps,
} from 'react-select-search';

import 'react-select-search/style.css';
import { useStore } from '../../hooks/use-store';
import { Sap } from '../../types/users-types';
import { KeyCodes } from '../../utils/dom-utils';
import './PracticeSelector.scss';

interface PracticeSelectorProps {
  disabled?: boolean;
}

const PracticeSelector = observer((props: PracticeSelectorProps) => {
  const { disabled } = props;
  const { globalStore, apiStore } = useStore();
  const [search, setSearch] = useState<string>('');
  const searchRef = useRef<HTMLInputElement>(null);
  const [filteredOptions, setFilteredOptions] = useState<Sap[]>([]);
  const [searchFocus, setSearchFocus] = useState<boolean>(false);
  const componentDisabled = disabled || globalStore.userSaps.length <= 1;

  useEffect(() => {
    setFilteredOptions(globalStore.userSaps);
  }, [globalStore.userSaps]);

  useEffect(() => {
    if (searchFocus) {
      searchRef.current?.focus();
    }
  }, [searchFocus]);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value || '');
    const valueToFilter = (e.target.value || '').trim().toLowerCase();
    const filtered =
      valueToFilter === ''
        ? globalStore.userSaps
        : globalStore.userSaps.filter(sap => {
            const { id, name } = sap;
            return (
              id?.toLowerCase().includes(valueToFilter) ||
              name?.toLowerCase().includes(valueToFilter)
            );
          });
    setFilteredOptions(filtered);
  };

  const renderSelectValue = (valueProps: ValueProps, _snapshot: Snapshot, className: string) => {
    const keydownHandler = (e: any) => {
      if (KeyCodes.DOM_VK_ESCAPE === e.keyCode) {
        setSearchFocus(false);
        e.stopPropagation();
        return;
      }
      valueProps.onKeyDown(e);
    };

    return (
      <>
        <input
          {...(valueProps as object)}
          className={className}
          ref={searchRef}
          style={{ display: searchFocus ? 'inline' : 'none' }}
          role='selector-search'
          type='text'
          value={search}
          placeholder='Enter Practice'
          onBlur={e => {
            setSearchFocus(false);
            valueProps.onBlur(e as any);
          }}
          onClick={e => e.stopPropagation()}
          onKeyDown={keydownHandler}
          onChange={handleSearch}
        />
        <div
          {...(valueProps as object)}
          role='selector-value'
          className={className}
          style={{
            display: searchFocus ? 'none' : 'block',
            cursor: componentDisabled ? 'default' : 'pointer',
          }}
          onClick={() => setSearchFocus(!componentDisabled)}
          onFocus={e => !componentDisabled && (valueProps as any).onFocus(e)}
        >
          <span className='spot-link--bold'>{apiStore.userName}</span>
          <span>
            {globalStore.selectedSap.name} ({globalStore.selectedSap.id})
          </span>
          <div
            className='down-arrow'
            style={{ visibility: componentDisabled ? 'hidden' : 'visible' }}
          />
        </div>
      </>
    );
  };

  const renderOption = (
    props1: DomProps,
    option: SelectedOption,
    _snapshot: OptionSnapshot,
    className: string,
  ) => {
    const { name, value } = option;
    return (
      // @ts-ignore
      <button
        {...props1}
        role='selector-option'
        className={className}
        type='button'
        onClick={() => setSearchFocus(false)}
      >
        <div>{value}</div>
        <span>{name}</span>
      </button>
    );
  };

  if (globalStore.userSaps.length === 0) return null;

  return (
    <div className='practice-selector_container'>
      <SelectSearch
        options={filteredOptions.map(us => ({ name: us.name, value: us.id }))}
        renderValue={renderSelectValue}
        renderOption={renderOption}
        onChange={value => globalStore.changeSelectedSap(value as string, false)}
        onBlur={() => {}}
        onFocus={() => {}}
        search={true}
        value={globalStore.selectedSap.id}
        disabled={componentDisabled}
      />
    </div>
  );
});

export default PracticeSelector;
